import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api, type PublicationsHubItem } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import FunnelMixBars from "@/components/FunnelMixBars";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PERIODS: { days: 7 | 14 | 30; label: string }[] = [
  { days: 7, label: "7 dni" },
  { days: 14, label: "14 dni" },
  { days: 30, label: "30 dni" },
];

const KIND_OPTS = [
  { value: "", label: "Wszystkie formaty" },
  { value: "carousel", label: "Karuzela" },
  { value: "feed_post", label: "Post" },
  { value: "czasotekst", label: "Czasotekst" },
];

const FUNNEL_OPTS = [
  { value: "", label: "Cały lejek" },
  { value: "tofu", label: "TOFU" },
  { value: "mofu", label: "MOFU" },
  { value: "bofu", label: "BOFU" },
];

function formatPct(v: number): string {
  return `${Math.round((v || 0) * 1000) / 10}%`;
}

function formatDay(iso: string): string {
  if (!iso || iso.length < 10) return iso || "-";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

function evalTone(label: string): string {
  if (label === "konwertuje" || label === "mocne") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (label === "słabo" || label === "ruch bez kliku") return "bg-rose-50 text-rose-800 border-rose-200";
  if (label === "klik bez koszyka" || label === "za wcześnie") return "bg-amber-50 text-amber-900 border-amber-200";
  return "bg-muted text-foreground border-border";
}

function kindLabel(kind: string): string {
  if (kind === "feed_post") return "post";
  if (kind === "czasotekst") return "czasotekst";
  return "karuzela";
}

function funnelBadgeVariant(funnel?: string): "secondary" | "brand" | "warning" | "outline" {
  if (funnel === "mofu") return "brand";
  if (funnel === "bofu") return "warning";
  if (funnel === "tofu") return "secondary";
  return "outline";
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}

function PublicationCard({ item }: { item: PublicationsHubItem }) {
  return (
    <Card className="border-border bg-white shadow-sm">
      <CardContent className="grid gap-4 p-4 md:grid-cols-[112px_1fr]">
        <div className="overflow-hidden rounded-md bg-muted">
          {item.thumb_url ? (
            <img
              src={item.thumb_url}
              alt=""
              className="aspect-[4/5] h-full w-full object-cover"
            />
          ) : (
            <div className="flex aspect-[4/5] items-center justify-center text-xs text-muted-foreground">
              brak miniatury
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{kindLabel(item.kind)}</Badge>
                <Badge variant={funnelBadgeVariant(item.funnel_stage)}>
                  {item.funnel_stage.toUpperCase()}
                </Badge>
                <span className="text-xs text-muted-foreground">{formatDay(item.published_at)}</span>
              </div>
              <h2 className="truncate text-base font-semibold">{item.title || item.id}</h2>
              {item.via ? (
                <p className="truncate text-xs text-muted-foreground">
                  via {item.via}
                  {item.short_url ? ` · ${item.short_url}` : ""}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {item.ig_permalink ? (
                <a
                  href={item.ig_permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                >
                  IG <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
              {item.fb_permalink ? (
                <a
                  href={item.fb_permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                >
                  FB <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
              <Link to={item.studio_href} className="text-xs text-brand hover:underline">
                Studio
              </Link>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-border/80 bg-muted/30 p-3">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Zasięg
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Reach" value={item.reach} />
                <Metric label="Save" value={item.saved} />
                <Metric label="Share" value={item.shares} />
                <Metric label="Koment." value={item.comments} />
                <Metric label="Save %" value={formatPct(item.save_rate)} />
                <Metric
                  label="Δ 1d"
                  value={item.views_delta_1d == null ? "-" : `+${item.views_delta_1d}`}
                />
              </div>
            </div>
            <div className="rounded-md border border-border/80 bg-muted/30 p-3">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Nasz lejek
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Metric label="Sesje" value={item.sessions} />
                <Metric label="Koszyki" value={item.checkouts} />
                <Metric label="Zakupy" value={item.purchases} />
                <Metric label="CVR koszyk" value={formatPct(item.cvr_checkout)} />
                <Metric label="CVR zakup" value={formatPct(item.cvr_purchase)} />
                <Metric label="Score" value={Math.round((item.score || 0) * 100) / 100} />
              </div>
            </div>
          </div>

          <div className={cn("rounded-md border px-3 py-2 text-sm", evalTone(item.eval_label))}>
            <span className="font-medium">{item.eval_label || "OK"}</span>
            {item.eval_text ? <span className="text-foreground/80"> · {item.eval_text}</span> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Publikacje() {
  const [days, setDays] = useState<7 | 14 | 30>(14);
  const [kind, setKind] = useState("");
  const [funnel, setFunnel] = useState("");

  const query = useQuery({
    queryKey: ["publications-hub", days, kind, funnel],
    queryFn: () =>
      api.publicationsHub({
        days,
        kind: kind || undefined,
        funnel_stage: funnel || undefined,
      }),
    retry: false,
  });

  const data = query.data;
  const kpis = data?.kpis;
  const planVs = data?.plan_vs_live;

  const syncLabel = useMemo(() => {
    if (!data?.synced_at) return "brak syncu Graph";
    return `Graph: ${formatDay(data.synced_at)} ${data.synced_at.slice(11, 16)}`;
  }, [data?.synced_at]);

  return (
    <div>
      <PageHeader
        title="Publikacje"
        subtitle="Jak poszło po publikacji ze studia (zasięg Meta + nasz lejek via)"
        actions={
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <Button
                key={p.days}
                size="sm"
                variant={days === p.days ? "default" : "outline"}
                onClick={() => setDays(p.days)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        }
      />

      <p className="mb-3 text-xs text-muted-foreground">{syncLabel}</p>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Ładuję hub…</p>
      ) : query.isError ? (
        <p className="text-sm text-rose-600">Nie udało się wczytać publikacji.</p>
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">Publikacje</div>
                <div className="text-2xl font-semibold tabular-nums">{kpis?.publications ?? 0}</div>
                <div className="text-xs text-muted-foreground">
                  Reach {kpis?.reach ?? 0} · Save {kpis?.saved ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">Zasięg</div>
                <div className="text-2xl font-semibold tabular-nums">{kpis?.impressions ?? 0}</div>
                <div className="text-xs text-muted-foreground">
                  Share {kpis?.shares ?? 0} · Kom. {kpis?.comments ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">Nasz lejek</div>
                <div className="text-2xl font-semibold tabular-nums">{kpis?.sessions ?? 0}</div>
                <div className="text-xs text-muted-foreground">
                  Koszyki {kpis?.checkouts ?? 0} · Zakupy {kpis?.purchases ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-white shadow-sm">
              <CardContent className="space-y-1 py-4">
                <div className="text-xs uppercase text-muted-foreground">CVR / score</div>
                <div className="text-2xl font-semibold tabular-nums">
                  {formatPct(kpis?.cvr_purchase ?? 0)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Mediana score {Math.round((kpis?.score_median ?? 0) * 100) / 100}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="mb-4 border-border bg-white shadow-sm">
            <CardContent className="py-4">
              <FunnelMixBars
                mix={{
                  pct: data?.funnel_mix?.pct,
                  target: data?.funnel_mix?.target,
                  warning: data?.funnel_mix?.warning,
                }}
                themeLine="Mix opublikowanych w oknie vs target 40/40/20"
              />
            </CardContent>
          </Card>

          {planVs?.show ? (
            <Card className="mb-4 border-border bg-white shadow-sm">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
                <p className="text-sm">
                  Plan vs live ({planVs.week}):{" "}
                  <span className="font-medium tabular-nums">
                    {planVs.published_studio}/{planVs.planned_studio}
                  </span>{" "}
                  studio opublikowane
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to={planVs.href}>Plan tygodnia</Link>
                </Button>
              </CardContent>
            </Card>
          ) : null}

          <div className="mb-4 flex flex-wrap gap-2">
            <Select
              value={kind}
              onChange={setKind}
              options={KIND_OPTS}
              className="w-[180px]"
              aria-label="Filtr formatu"
            />
            <Select
              value={funnel}
              onChange={setFunnel}
              options={FUNNEL_OPTS}
              className="w-[160px]"
              aria-label="Filtr lejka"
            />
          </div>

          <div className="space-y-3">
            {(data?.items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Brak opublikowanych pozycji ze studia w tym oknie.
              </p>
            ) : (
              (data?.items || []).map((item) => <PublicationCard key={item.id} item={item} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}
